<template>
  <section ref="root" :class="$style['years']" aria-labelledby="years-text">
    <div :class="$style['years__number']" aria-hidden="true">
      <span v-for="(digit, i) in digits" :key="i">{{ digit }}</span>
    </div>
    <p id="years-text" :class="$style['years__text']">
      Sinds 1953 maakt de Hengelose Revue voorstellingen, gespeeld door mensen uit Hengelo en omgeving.
    </p>
    <ul :class="$style['years__shows']">
      <li v-for="show in shows" :key="show.year">
        <NuxtLink :to="show.to"><b>{{ show.year }}</b>{{ show.title }}</NuxtLink>
      </li>
    </ul>
  </section>
</template>
<script setup lang="ts">
// A giant year counts up from 1953 to this season while you scroll, with the past shows underneath
const props = defineProps<{ year: number, title: string }>()

const FOUNDED = 1953
const { prefersReducedMotion, register, scheduleRefresh } = useStageMotion()

// The photo albums are named like "2026 - Oost, West, Thuis...?"
const albums = await useHrStoryblok('cdn/links', { starts_with: 'impressie/', per_page: 100 })
const shows = computed(() => {
  const list = Object.values((albums as any)?.links ?? {})
    .map((link: any) => {
      const match = /^(\d{4})\s*-\s*(.+)$/.exec(link.name ?? '')
      return match ? { year: Number(match[1]), title: match[2]!.replace(/\.\.\./g, '…'), to: `/${link.slug}` } : undefined
    })
    .filter((show): show is { year: number, title: string, to: string } => Boolean(show))
  if (!list.some(show => show.year === props.year)) {
    list.push({ year: props.year, title: props.title, to: '#avond' })
  }
  return list.sort((a, b) => a.year - b.year)
})

const digits = ref(String(FOUNDED).split(''))
const root = ref<HTMLElement>()
let context: ReturnType<ReturnType<typeof register>['gsap']['context']> | undefined

onMounted(() => {
  if (prefersReducedMotion()) return
  const { gsap } = register()
  const count = { value: FOUNDED }
  context = gsap.context(() => {
    gsap.to(count, {
      value: props.year,
      ease: 'none',
      scrollTrigger: { trigger: root.value, start: 'top top', end: '+=110%', scrub: 0.3, pin: true, refreshPriority: 1 },
      onUpdate: () => { digits.value = String(Math.round(count.value)).split('') }
    })
  }, root.value)
  scheduleRefresh()
})

onBeforeUnmount(() => context?.revert())
</script>
<style module>
.years {
  display: grid;
  place-items: center;
  align-content: center;
  gap: var(--spacing-xxl);
  min-height: 100svh;
  padding: var(--stage-section) var(--stage-gutter);
  background: var(--secondary-color);
  color: var(--stage-white);
  text-align: center;
}

.years__number {
  display: flex;
  justify-content: center;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(7rem, 30vw, 30rem);
  line-height: 0.8;
  letter-spacing: -0.04em;
}

/* Every digit gets the same width, so the number does not wobble while it counts */
.years__number span {
  display: inline-block;
  width: 0.66em;
  text-align: center;
}

.years__text {
  max-width: 38rem;
  margin: 0;
  font-size: clamp(1.125rem, 1rem + 0.6vw, 1.5rem);
  line-height: 1.45;
}

.years__shows {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing) var(--spacing-xl);
  max-width: 60rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.years__shows a {
  color: oklch(100% 0 0deg / 80%);
  text-decoration: none;
}

.years__shows a:hover {
  color: var(--stage-white);
  text-decoration: underline;
  text-underline-offset: 0.3em;
}

.years__shows b {
  margin-right: 0.4em;
  color: var(--stage-white);
}
</style>
